// Conversation Analysis Trigger
// Shows opt-in popup after completing a ConnectieFlow phase

import React, { useState } from 'react';
import { X, Coins } from 'lucide-react';
import { InsightIcon, DocumentIcon, ICON_STYLES } from './icons';
import { requestConversationAnalysis } from '../services/conversationAnalysisService';
import { getCreditBalance } from '../services/creditsService';
import { CREDIT_COSTS } from '../types/credits.types';

interface ConversationAnalysisTriggerProps {
  userId: string;
  connectionId: string;
  phase: 'chat' | 'call' | 'video' | 'physical';
  onClose: () => void;
  onSuccess: () => void;
}

const ConversationAnalysisTrigger: React.FC<ConversationAnalysisTriggerProps> = ({
  userId,
  connectionId,
  phase,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  React.useEffect(() => {
    loadBalance();
  }, [userId]);

  const loadBalance = async () => {
    try {
      const creditData = await getCreditBalance(userId);
      setBalance(creditData?.balance ?? 0);
    } catch (err) {
      console.error('Error loading balance:', err);
    }
  };

  const handleRequestAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await requestConversationAnalysis(userId, connectionId, phase);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Analyse mislukt');
      }
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const hasEnoughCredits = balance !== null && balance >= CREDIT_COSTS.CONVERSATION_ANALYSIS;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-orange-600 p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <InsightIcon size={24} weight="regular" />
              <h2 className="text-xl font-medium">Gesprek Inzichten</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              disabled={loading}
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-sm opacity-90">
            Automatische samenvatting van je {phase} gesprek
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Features */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start space-x-3">
              <DocumentIcon size={18} weight="regular" className="text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-700 text-sm">Samenvatting & Key Points</p>
                <p className="text-xs text-gray-500">Belangrijkste punten automatisch geëxtraheerd</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DocumentIcon size={18} weight="regular" className="text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-700 text-sm">Actiepunten & Groeipunten</p>
                <p className="text-xs text-gray-500">Concrete stappen voor vervolgacties</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DocumentIcon size={18} weight="regular" className="text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-700 text-sm">Topic & Entity Extractie</p>
                <p className="text-xs text-gray-500">Automatisch topics, tools en bedrijven herkennen</p>
              </div>
            </div>
          </div>

          {/* Cost Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Kosten</span>
              <div className="flex items-center space-x-1">
                <Coins size={16} className="text-orange-600" />
                <span className="font-medium text-gray-700">{CREDIT_COSTS.CONVERSATION_ANALYSIS} credits</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Je saldo</span>
              <span className={`font-medium ${hasEnoughCredits ? 'text-emerald-600' : 'text-red-600'}`}>
                {balance !== null ? `${balance} credits` : '...'}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Warning */}
          {!hasEnoughCredits && balance !== null && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Onvoldoende credits. Je kunt credits verdienen door te verwijzen of kopen in de instellingen.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Later
            </button>
            <button
              onClick={handleRequestAnalysis}
              disabled={loading || !hasEnoughCredits}
              className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Analyseren...' : 'Analyseer nu'}
            </button>
          </div>

          {/* Info */}
          <p className="text-xs text-gray-500 text-center mt-3">
            Resultaat verschijnt in je Inbox onder "Samenvattingen"
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConversationAnalysisTrigger;
